import { cn } from '@/lib/cn';
import { socialsFor } from './socials';
import { useStreamerProfile } from './api';

/**
 * The card that says whose site this is.
 *
 * It is the only thing on the home page that distinguishes a streamer's
 * community from an affiliate landing page — the partner logos above it say
 * nothing about who is behind them.
 *
 * Follows the owner's reference: a ringed portrait on the left with the LIVE
 * badge clipped to its lower corner, and a SPEECH BUBBLE on the right carrying
 * the greeting and the bio, with the socials and the Kick button beneath it.
 * The bubble is what makes the paragraph read as the streamer talking rather
 * than as site copy, so its tail is not decoration to drop on small screens —
 * it just changes edge.
 *
 * Everything here comes from the API. It used to be build-time configuration,
 * which was right until the LIVE badge had to be switched from the panel — a
 * `VITE_` variable cannot change without a redeploy.
 */
export function StreamerCard() {
  const { data: streamer, isLoading } = useStreamerProfile();

  if (isLoading) {
    return <div className="h-72 animate-pulse rounded-[var(--radius-card)] bg-surface-900/60" />;
  }

  if (!streamer) {
    return null;
  }

  const socials = socialsFor(streamer.socials);

  return (
    <section
      id="topluluk"
      aria-labelledby="streamer-intro"
      className="streamer-panel overflow-hidden rounded-[var(--radius-card)] border border-surface-800"
    >
      <div className="relative flex flex-col items-center gap-6 p-6 sm:p-9 lg:flex-row lg:items-center lg:gap-10">
        {/* The gold wash behind the portrait. Decorative, so it is hidden rather
            than described — the heading beside it already carries the meaning. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background:
              'radial-gradient(60% 90% at 22% 45%, oklch(0.78 0.16 86 / 0.16), transparent 62%)',
          }}
        />

        <Portrait streamer={streamer} />

        <div className="relative flex min-w-0 flex-1 flex-col items-center gap-5 lg:items-start">
          <Bubble>
            <h2
              id="streamer-intro"
              className="flex flex-wrap items-center justify-center gap-2 text-xl font-extrabold tracking-tight text-brand-400 sm:text-2xl lg:justify-start"
            >
              Merhaba, ben {streamer.displayName}!
              <CrownGlyph />
            </h2>

            {streamer.bio ? (
              <p className="mt-3 max-w-prose text-center text-base leading-relaxed text-surface-300 lg:text-left">
                {streamer.bio}
              </p>
            ) : null}
          </Bubble>

          {socials.length > 0 ? (
            <ul className="flex flex-wrap items-center justify-center gap-3 lg:justify-start">
              {socials.map((social) => (
                <li key={social.platform}>
                  <a
                    href={social.url}
                    target="_blank"
                    // noopener is the one that matters: without it the opened
                    // page gets a handle on this window and can navigate it.
                    rel="noreferrer noopener"
                    aria-label={`${streamer.displayName} · ${social.label}`}
                    className={cn(
                      'grid size-11 place-items-center rounded-xl transition',
                      'hover:scale-110 motion-reduce:hover:scale-100',
                    )}
                    style={{ background: social.background, color: social.foreground }}
                  >
                    <social.Icon />
                  </a>
                </li>
              ))}
            </ul>
          ) : null}

          {streamer.kickUrl ? (
            <a
              href={streamer.kickUrl}
              target="_blank"
              rel="noreferrer noopener"
              className={cn(
                'inline-flex min-h-12 w-full items-center justify-center gap-2.5 rounded-xl px-8',
                'gf-button gf-button-primary text-sm',
                'shadow-lg shadow-brand-500/20 transition hover:brightness-110',
                'sm:w-auto',
              )}
            >
              {/* The copy follows the badge. Sending someone to "watch me live"
                  when the stream is off is the same broken promise as a LIVE
                  badge over a dark channel. */}
              {streamer.isLive ? "Kick'te canlı izle" : "Kick'te takip et"}
              <ArrowGlyph />
            </a>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function Portrait({
  streamer,
}: {
  streamer: { displayName: string; avatarUrl: string | null; isLive: boolean };
}) {
  return (
    <div className="relative shrink-0">
      <span
        className="block rounded-full p-[3px]"
        style={{ background: 'linear-gradient(150deg, #f5d97a, #e9be3f 40%, #7a5f16)' }}
      >
        {streamer.avatarUrl ? (
          <img
            src={streamer.avatarUrl}
            alt=""
            width={176}
            height={176}
            // Above the fold on a phone, so it is worth fetching early.
            fetchPriority="high"
            className="size-28 rounded-full object-cover sm:size-32 lg:size-36"
          />
        ) : (
          <span className="grid size-28 place-items-center rounded-full bg-surface-800 text-3xl font-extrabold text-surface-500 sm:size-32 lg:size-36">
            {streamer.displayName.slice(0, 1).toUpperCase()}
          </span>
        )}
      </span>

      {streamer.isLive ? (
        // Clipped to the lower-left of the ring, as in the reference. `bottom-1`
        // rather than a negative offset: the card's own rounded corner clips
        // anything that leaves it, and this sits close to that corner.
        <span className="absolute bottom-1 -left-2 flex items-center gap-1.5 rounded-lg bg-live-500 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-lg shadow-black/40">
          {/*
            The pulse is decoration, and the global reduced-motion rule collapses
            it — which is fine, because the word LIVE carries the meaning, not
            the dot.
          */}
          <span aria-hidden className="size-1.5 rounded-full bg-white motion-safe:animate-pulse" />
          Live
        </span>
      ) : null}
    </div>
  );
}

/**
 * The speech bubble.
 *
 * The tail is a rotated square rather than a border trick, so it inherits the
 * same background and border and stays put when the bubble's height changes. It
 * points left from `lg` and up below it, because that is where the portrait is
 * at each width.
 */
function Bubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative w-full rounded-2xl border border-surface-700/70 bg-surface-950/50 p-5 sm:p-6">
      <span
        aria-hidden
        className={cn(
          'absolute size-4 rotate-45 border-surface-700/70 bg-surface-950/50',
          // Below lg the portrait is above, so the tail points up from the top edge.
          '-top-2 left-1/2 -translate-x-1/2 border-l border-t',
          // From lg it points left, at the height of the greeting.
          'lg:left-auto lg:top-9 lg:-left-2 lg:translate-x-0 lg:border-l lg:border-t lg:border-r-0 lg:border-b-0',
        )}
      />
      {children}
    </div>
  );
}

function CrownGlyph() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-5 shrink-0 fill-brand-400">
      <path d="M3 8l4 3 5-6 5 6 4-3-2 11H5L3 8z" />
    </svg>
  );
}

function ArrowGlyph() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-4 fill-current">
      <path d="M13 5l7 7-7 7v-4H4v-6h9V5z" />
    </svg>
  );
}
